import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, AlertTriangle, UsersRound } from "lucide-react";

/**
 * Full team reassignment modal (Design Head / Admin / Director).
 * Requires a REASON (6-500 chars). Reason stored in reassignment_history
 * and visible only to Admin/Director/Design Head roles.
 *
 * Per Feb 2026 spec: Design Head assigns to a Design Manager only.
 * The Design Manager IS the designer on the project.
 */
export default function ReassignDesignTeamModal({ open, project, onClose, onSaved }) {
  const [managers, setManagers] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [managerId, setManagerId] = useState("");
  const [designerId, setDesignerId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const loadManagers = useCallback(async () => {
    try {
      const { data } = await api.get("/design/managers");
      setManagers(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  useEffect(() => {
    if (!open) return;
    setErr(""); setReason("");
    setManagerId(project?.design_manager_id || "");
    setDesignerId(project?.interior_designer_id || "");
    loadManagers();
  }, [open, project, loadManagers]);

  // Load designers reporting to the chosen manager
  useEffect(() => {
    if (!managerId) { setDesigners([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/design/designers?manager_id=${managerId}`);
        setDesigners(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, [managerId]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 6) { setErr("Reason must be at least 6 characters"); return; }
    if (!managerId) { setErr("Design Manager is required"); return; }
    setSaving(true);
    try {
      await api.post(`/design/projects/${project.id}/reassign-full`, {
        design_manager_id: managerId,
        interior_designer_id: designerId || null,
        reason: reason.trim(),
      });
      toast.success(designerId ? "Team reassigned" : "Design Manager reassigned");
      onSaved();
    } catch (er) {
      setErr(formatApiErrorDetail(er.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="reassign-full-modal">
      <div className="w-full max-w-lg iz-card p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <div className="flex items-center gap-3 mb-4">
          <UsersRound className="w-5 h-5 text-[#d4af37]"/>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Team Reassignment</p>
            <h3 className="font-display text-xl text-white">{project?.project_name}</h3>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Design Manager *</label>
            <select value={managerId} onChange={(e) => { setManagerId(e.target.value); setDesignerId(""); }} className="iz-input" required data-testid="reassign-manager-select">
              <option value="">— Select Design Manager —</option>
              {managers.map((m) => (<option key={m.id} value={m.id}>{m.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Interior Designer (optional)</label>
            <select value={designerId} onChange={(e) => setDesignerId(e.target.value)} className="iz-input" disabled={!managerId} data-testid="reassign-designer-select">
              <option value="">{managerId ? "— Leave with Design Manager —" : "Pick a manager first"}</option>
              {designers.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              Leave blank → Design Manager owns the project. Pick a designer → ownership shifts to the Interior Designer.
            </p>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Reason for change *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="iz-input"
              rows={3}
              minLength={6}
              maxLength={500}
              required
              placeholder="Why is this team change happening? (visible only to Admin, Directors, Design Head)"
              data-testid="reassign-reason-input"
            />
            <p className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-[#d4af37]"/> Stored for audit — only Admin, Directors, and Design Head can view.
            </p>
          </div>
          {err && <p className="text-xs text-red-400 border border-red-500/40 bg-red-500/5 rounded px-3 py-2">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="iz-btn-secondary text-xs" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary text-xs disabled:opacity-50" data-testid="reassign-submit-btn">
              {saving ? "Saving…" : "Confirm Reassignment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
